import {IAccountant} from "api/types";
import {MapPin, Clock, ExternalLink} from "lucide-react";
import {extractUrl} from "pages/accountant/Profile/utils";
import ProfileStatusBar from "pages/accountant/Profile/components/ProfileStatusBar";
import Certification from "pages/accountant/Profile/components/ProfileCard/Certification";
import Review from "pages/accountant/Profile/components/ProfileCard/Review";
import Section from "pages/accountant/Profile/components/ProfileCard/Section";
import {FC, useState} from "react";
import {useNavigate} from "react-router";
import {activityStatusByDate} from "utils/activityStatusByDate";
import {ACCOUNTANT_BIO_TRUNCATE_LENGTH} from "pages/accountant/Profile/constants";

interface Props {
  user: IAccountant;
}

const AccountantShortProfile: FC<Props> = ({user}) => {
  const {city, state} = user;
  const certEntries = Object.entries(user.certifications || {});
  const reviewEntries = Object.entries(user.reviews || {});
  const navigate = useNavigate();
  const [bioExpanded, setBioExpanded] = useState(false);

  const knowledge = user.skillScore > 80 ? "high" : user.skillScore > 20 ? "average" : "low";
  const activity = activityStatusByDate(user.xpData.lastActivity || user.xpData.dateJoined);
  const speed = user.speed;

  const locationStr = [city?.toLowerCase(), state?.toUpperCase()].filter(Boolean).join(", ");
  const bio = user.bio || "";
  const bioTruncated =
    bio.length > ACCOUNTANT_BIO_TRUNCATE_LENGTH && !bioExpanded
      ? bio.slice(0, ACCOUNTANT_BIO_TRUNCATE_LENGTH) + "…"
      : bio;

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="px-5 py-4 border-b border-brand-border bg-brand-surface flex-shrink-0">
        <p className="text-xs text-brand-muted mb-0.5">About</p>
        <button
          className="text-base font-semibold text-brand-navy hover:text-brand-amber transition-colors flex items-center gap-1.5 group"
          onClick={() => navigate(`/accountant/${user.id}`)}
        >
          {user.name}
          <ExternalLink className="w-3.5 h-3.5 opacity-0 group-hover:opacity-100 transition-opacity" />
        </button>
      </div>

      {/* Body */}
      <div className="flex-1 overflow-y-auto">
        <div className="flex flex-col p-4 gap-4">
          {/* Score card — matches recruiter/admin accountant profile */}
          <ProfileStatusBar knowledge={knowledge} activity={activity} speed={speed} single />

          {/* Quick facts */}
          <div className="bg-brand-cream rounded-xl overflow-hidden divide-y divide-brand-border">
            {!user.enterprise && user.employmentType && (
              <div className="flex items-center gap-2.5 px-4 py-3">
                <Clock className="w-3.5 h-3.5 text-brand-muted shrink-0" />
                <span className="text-xs text-brand-muted grow">Open to</span>
                <span className="text-xs font-medium text-brand-navy">{user.employmentType}</span>
              </div>
            )}
            {locationStr && (
              <div className="flex items-center gap-2.5 px-4 py-3">
                <MapPin className="w-3.5 h-3.5 text-brand-muted shrink-0" />
                <span className="text-xs text-brand-muted grow">Location</span>
                <span className="text-xs font-medium text-brand-navy capitalize">{locationStr}</span>
              </div>
            )}
          </div>

          {/* Bio with Read more / Show less */}
          {bio && (
            <div className="bg-brand-cream rounded-xl px-4 py-3">
              <p className="text-xs font-semibold text-brand-muted uppercase tracking-widest mb-2">About</p>
              <p className="text-xs text-brand-navy leading-relaxed break-words">
                {bioTruncated}
                {bio.length > ACCOUNTANT_BIO_TRUNCATE_LENGTH && (
                  <button
                    onClick={() => setBioExpanded((v) => !v)}
                    className="ml-1 text-xs font-medium text-brand-amber hover:opacity-70 transition-opacity underline underline-offset-2"
                  >
                    {bioExpanded ? "Show less" : "Read more"}
                  </button>
                )}
              </p>
            </div>
          )}

          {/* Credentials & Badges — same layout as normal profile */}
          {certEntries.length > 0 && (
            <div>
              <p className="text-xs font-semibold uppercase tracking-widest text-brand-muted mb-2 px-1">
                Credentials &amp; Badges
              </p>
              <div className="flex flex-wrap gap-2">
                {certEntries.map(([name, val]) => (
                  <Certification key={name} name={name} url={extractUrl(val)} />
                ))}
              </div>
            </div>
          )}

          {/* Software */}
          {Object.keys(user.softwareExperience || {}).length > 0 && (
            <div className="bg-brand-cream rounded-xl px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-widest text-brand-muted mb-2">Software</p>
              <div className="flex flex-wrap gap-1.5">
                {Object.keys(user.softwareExperience).map((s) => (
                  <span
                    key={s}
                    className="text-xs px-2.5 py-1 rounded-full bg-white border border-brand-border text-brand-navy"
                  >
                    {s}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Reviews */}
          {reviewEntries.length > 0 && (
            <Section header="Reviews" initialExpanded>
              <div className="flex flex-col gap-2 px-4 pb-3">
                {reviewEntries.map(([name, val]) => (
                  <Review key={name} name={name} url={extractUrl(val)} />
                ))}
              </div>
            </Section>
          )}

          {/* Email */}
          {user.email && (
            <div className="bg-brand-cream rounded-xl px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-widest text-brand-muted mb-1">Email</p>
              <p className="text-xs font-medium text-brand-navy">{user.email}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountantShortProfile;
